import type { ChoiceOption, SubOption } from '@/api/types/Forms'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { ChoiceMark, ChoiceRow, choiceFrame } from '@/ui/core/choice'
import { Rise, Swivel } from '@/ui/core/motion'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useRef, useState } from 'react'
import { TextInput, View } from 'react-native'
import { StepLayout } from '../StepLayout'
import {
	type ChoiceState,
	canProceed,
	getChoiceState,
	resolveButtonLabel,
	setEditableChoice,
	toggleChoice,
} from '../logic/stepState'
import { PrimaryStepButton } from './PrimaryStepButton'
import type { StepProps } from './types'

const SelectableOption = ({
	option,
	isSelected,
	multi,
	onPress,
}: {
	option: ChoiceOption
	isSelected: boolean
	multi: boolean
	onPress: () => void
}) => (
	<ChoiceRow checked={isSelected} multi={multi} onPress={onPress}>
		<Text weight="bold" tone={isSelected ? 'white' : 'ink'} className="flex-1">
			{option.title}
		</Text>
	</ChoiceRow>
)

/**
 * The one option that is typed rather than picked: "Other", and the free text beside it.
 *
 * It wears the same row as everything above it, because on the web it was never a thing apart —
 * a bare field among bordered rows reads as a different kind of object and asks a different kind
 * of question. Here the row is the field's own frame, so the field brings no box of its own; the
 * mark fills as soon as there is something in it, and tapping anywhere on the row starts typing.
 */
const EditableOption = ({
	option,
	value,
	multi,
	onChangeText,
	onSubmit,
}: {
	option: ChoiceOption
	value: string
	multi: boolean
	onChangeText: (value: string) => void
	onSubmit: () => void
}) => {
	const field = useRef<TextInput>(null)
	const checked = value.trim().length > 0

	return (
		<ChoiceRow
			checked={checked}
			multi={multi}
			field
			onPress={() => field.current?.focus()}
		>
			<TextInput
				ref={field}
				value={value}
				onChangeText={onChangeText}
				placeholder={option.title}
				accessibilityLabel={option.title}
				// The prompt is the option's own label, so it is set like every other label here and
				// not like a hint: same size, same weight, same ink. Grey would make the one typed
				// answer look like a lesser kind of answer than the ones beside it.
				placeholderTextColor={checked ? colors.white : colors.ink}
				returnKeyType="done"
				onSubmitEditing={onSubmit}
				// body1 bold, which is what `Text` gives the picked options beside it: they set no
				// size, so they take the default, and a field set at body0 stood two points taller
				// than every label around it.
				className="flex-1 font-bold text-body1"
				style={{ color: checked ? colors.white : colors.ink, paddingVertical: 0 }}
			/>
		</ChoiceRow>
	)
}

const SubOptionRow = ({
	subOption,
	isSelected,
	onPress,
}: {
	subOption: SubOption
	isSelected: boolean
	onPress: () => void
}) => (
	<Pressable
		onPress={onPress}
		accessibilityRole="checkbox"
		accessibilityState={{ checked: isSelected }}
		className="py-2.5"
	>
		<HStack className="items-center" gap={12}>
			<ChoiceMark checked={isSelected} multi />
			<Text className="flex-1">{subOption.title}</Text>
		</HStack>
	</Pressable>
)

/**
 * A named group of conditions, opened to answer. Closed it is just its name, so a patient with
 * nothing to declare can pass the whole group without opening it.
 *
 * The chevron turns as the score card's does, which is what an expanding thing does in this app.
 */
const ExpandableSelectOption = ({
	option,
	choice,
	onToggle,
}: {
	option: ChoiceOption
	choice: ChoiceState
	onToggle: (id: number) => void
}) => {
	const subOptions = option.subOptions ?? []
	const selectedCount = subOptions.filter((sub) => String(sub.id) in choice).length
	// Opened by hand, or already open because something inside it was chosen earlier.
	const [isExpanded, setIsExpanded] = useState(selectedCount > 0)

	return (
		// The group stays white so the rows inside it stay readable.
		<View className={choiceFrame(false)}>
			<Pressable
				onPress={() => setIsExpanded((expanded) => !expanded)}
				accessibilityRole="button"
				accessibilityState={{ expanded: isExpanded }}
			>
				<HStack className="items-center" gap={12}>
					<Text weight="bold" className="flex-1">
						{option.title}
					</Text>
					<Swivel deg={isExpanded ? -90 : 0}>
						<Glyph name="chevron" size={16} color={colors.ink} />
					</Swivel>
				</HStack>
			</Pressable>
			{isExpanded && (
				// Open, the group's name is a heading over its conditions rather than the first line
				// of them, so it keeps a little air under it.
				<VStack className="mt-3">
					{subOptions.map((subOption, index) => (
						<Rise key={subOption.id} index={index}>
							<SubOptionRow
								subOption={subOption}
								isSelected={String(subOption.id) in choice}
								onPress={() => onToggle(subOption.id)}
							/>
						</Rise>
					))}
				</VStack>
			)}
		</View>
	)
}

export const ChoiceStep = ({
	step,
	state,
	onChange,
	onNext,
	onBack,
	onClose,
	progress,
	isSubmitting,
}: StepProps<ChoiceState>) => {
	const choice = getChoiceState(state)
	const options = step.options ?? []
	const isMultiSelect = !!step.isMultiSelect

	/**
	 * A tap marks the answer and nothing else. The step moves on when the button is pressed.
	 *
	 * It used to answer and turn the page at once, which is quick and gives nothing back: no way
	 * to look at what was picked, no way to change it without going back a step, and a screen that
	 * changes out from under anyone listening rather than looking. One behaviour for everyone, and
	 * the same one the multi-selects already had.
	 */
	const onToggle = (id: number) => {
		onChange(isMultiSelect ? toggleChoice(choice, id) : { [String(id)]: {} })
	}

	const onEditableChange = (id: number, value: string) => {
		onChange(
			isMultiSelect
				? setEditableChoice(choice, id, value)
				: setEditableChoice({}, id, value)
		)
	}

	return (
		<StepLayout
			title={step.title}
			description={step.description}
			progress={progress}
			onBack={onBack}
			onClose={onClose}
			cta={
				<PrimaryStepButton
					label={resolveButtonLabel(step, choice)}
					onPress={onNext}
					disabled={!canProceed(step, choice)}
					isLoading={isSubmitting}
				/>
			}
		>
			<VStack
				gap={8}
				accessibilityRole={isMultiSelect ? undefined : 'radiogroup'}
				accessibilityLabel={step.title}
			>
				{options.map((option, index) => {
					// Options arrive one after another, the way every list in this app does.
					const rise = (node: React.ReactNode) => (
						<Rise key={option.id} index={index}>
							{node}
						</Rise>
					)

					switch (option.type) {
						case 'SELECTABLE':
							return rise(
								<SelectableOption
									option={option}
									isSelected={String(option.id) in choice}
									multi={isMultiSelect}
									onPress={() => onToggle(option.id)}
								/>
							)
						case 'EDITABLE':
							return rise(
								<EditableOption
									option={option}
									value={choice[String(option.id)]?.value ?? ''}
									multi={isMultiSelect}
									onChangeText={(value) => onEditableChange(option.id, value)}
									onSubmit={() => canProceed(step, choice) && onNext()}
								/>
							)
						case 'EXPANDABLE_SELECT':
							return rise(
								<ExpandableSelectOption
									option={option}
									choice={choice}
									onToggle={onToggle}
								/>
							)
					}
				})}
			</VStack>
		</StepLayout>
	)
}
